import { forwardRef } from 'react';
import styles from './EntryChip.module.css';

interface EntryChipProps {
  /** "Dec 11" / "Dec 11, 2027" / "Mon" */
  primaryLabel: string;
  /** "All day" / "6 AM - 7 PM" — clicking this opens the time-range popover. */
  timeLabel: string;
  onOpenTimeRange: () => void;
  onRemove: () => void;
  /** Highlights the chip while its popover is open. */
  active?: boolean;
  disabled?: boolean;
}

/** One selected date/day chip: its label, a clickable time segment that
 * opens the time-range popover, and a remove button. Forwards its ref to the
 * root element because the popover's trigger needs a DOM node to align to. */
const EntryChip = forwardRef<HTMLDivElement, EntryChipProps>(
  (
    {
      primaryLabel,
      timeLabel,
      onOpenTimeRange,
      onRemove,
      active = false,
      disabled = false,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={[styles.chip, active ? styles.active : '']
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.primaryLabel}>{primaryLabel}</span>
        <button
          type='button'
          className={styles.timeLabel}
          onClick={onOpenTimeRange}
          disabled={disabled}
        >
          {timeLabel}
        </button>
        <button
          type='button'
          className={styles.remove}
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${primaryLabel}`}
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 14 14'
            fill='none'
            aria-hidden
          >
            <path
              d='M10.5 4.205L9.795 3.5L7 6.295L4.205 3.5L3.5 4.205L6.295 7L3.5 9.795L4.205 10.5L7 7.705L9.795 10.5L10.5 9.795L7.705 7L10.5 4.205Z'
              fill='currentColor'
            />
          </svg>
        </button>
      </div>
    );
  }
);
EntryChip.displayName = 'EntryChip';

export default EntryChip;
